import { useMemo } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetStaffUsersQuery } from '@/services/api/userService';
import { getUserDisplayName } from '@/utils/user';

import { NONE_OPTION_ID } from './types';

type Props = {
  value: string | null | undefined;
  onChange: (userId: string) => void;
  disabled?: boolean;
  id?: string;
  labelId?: string;
};

export const ParalegalSelect = ({
  value,
  onChange,
  disabled,
  id,
  labelId,
}: Props) => {
  // Always fetch all active users regardless of firm
  const { data: users = [], isLoading, error } = useGetStaffUsersQuery();

  const options = useMemo(
    () =>
      users.map((user) => ({
        id: user.id,
        label:
          user.first_name && user.last_name
            ? `${user.first_name} ${user.last_name}${user.email ? ` (${user.email})` : ''}`
            : user.email || user.username || 'Unknown User',
      })),
    [users],
  );

  const handleChange = (val: string) => {
    // When "None" is selected, send empty string to trigger validation
    if (val === NONE_OPTION_ID) {
      onChange('');
    } else {
      onChange(val);
    }
  };
  const placeholder = useMemo(() => {
    const user = users.find((u) => u.id === value);
    // Show placeholder when no value, empty string, or NONE selected
    if (!value || value === NONE_OPTION_ID || !user) {
      return 'Select managing paralegal';
    }
    return getUserDisplayName(user);
  }, [value, users]);

  // Show loading or error states
  if (error) {
    return <div className="text-sm text-red-500">Error loading users</div>;
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <Select
          value={value ?? undefined}
          onValueChange={handleChange}
          disabled={disabled || isLoading}
        >
          <SelectTrigger
            id={id}
            className="w-full"
            aria-label="Select managing paralegal"
            aria-labelledby={labelId}
          >
            <SelectValue placeholder={isLoading ? 'Loading...' : placeholder} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_OPTION_ID}>None</SelectItem>
            {options.length === 0 && !isLoading && (
              <SelectItem value="__empty__" disabled>
                No active users found
              </SelectItem>
            )}
            {options.map((opt) => (
              <SelectItem key={opt.id} value={opt.id}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
